import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "@/lib/browserRouter";
import { pickLbApiBase } from "@/lib/pickLbApi";
import { Button } from "@/shadcn/components/ui/button";
import { logException } from "@/utils/sentryLogger";

/**
 * Hosted upsell/downsell when the funnel step has no external pageUrl.
 * Query: pid, ft (funnel token), fstep (step id).
 */
export default function FunnelContinue() {
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get("pid") || "";
  const token = searchParams.get("ft") || "";
  const stepId = searchParams.get("fstep") || "";

  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  /** After Sim/Não is submitted once, buttons stay off (prevents double charge). */
  const [choiceLocked, setChoiceLocked] = useState(false);
  const choiceLockRef = useRef(false);

  const apiBase = pickLbApiBase();

  useEffect(() => {
    if (!purchaseId || !token || !stepId || !apiBase) {
      setErr("Parâmetros inválidos ou API não configurada.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = new URLSearchParams({
          purchaseId,
          token,
          stepId,
        });
        const res = await fetch(
          `${apiBase.replace(/\/$/, "")}/funnel/session/context?${q}`,
          { method: "GET" }
        );
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "context_failed");
        }
        if (!cancelled) setCtx(data);
      } catch (e) {
        if (!cancelled) {
          setErr("Não foi possível carregar a oferta. Tente novamente.");
          logException(e, { area: "FunnelContinue", tags: { purchaseId } });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [purchaseId, token, stepId, apiBase]);

  const postAction = useCallback(
    async (outcome) => {
      if (!purchaseId || !token || !stepId || !apiBase) return;
      if (choiceLockRef.current || busy) return;
      choiceLockRef.current = true;
      setChoiceLocked(true);
      setBusy(true);
      setErr("");
      try {
        const res = await fetch(
          `${apiBase.replace(/\/$/, "")}/funnel/session/action`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              purchaseId,
              token,
              currentStepId: stepId,
              outcome,
            }),
          }
        );
        const data = await res.json();
        if (!res.ok || !data.success) {
          if (data.error === "no_card_on_file" && outcome === "yes") {
            setErr(
              "Este pagamento não permite cobrança automática no cartão (ex.: Pix). Toque em recusar para continuar."
            );
            choiceLockRef.current = false;
            setChoiceLocked(false);
            setBusy(false);
            return;
          }
          throw new Error(data.error || "action_failed");
        }
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
        }
      } catch (e) {
        setErr(
          outcome === "yes"
            ? "Não foi possível concluir a compra. Recarregue a página se precisar tentar novamente."
            : "Erro ao continuar. Recarregue a página se precisar tentar novamente."
        );
        logException(e, { area: "FunnelContinue", tags: { outcome } });
      } finally {
        setBusy(false);
      }
    },
    [purchaseId, token, stepId, apiBase, busy]
  );

  if (err && !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-center text-muted-foreground">{err}</p>
      </div>
    );
  }

  if (!ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-6 max-w-lg mx-auto">
      <div className="text-center space-y-2">
        {ctx.productName ? (
          <h1 className="text-xl font-semibold">{ctx.productName}</h1>
        ) : null}
        {ctx.priceLabel ? (
          <p className="text-2xl font-bold text-primary">{ctx.priceLabel}</p>
        ) : null}
      </div>
      {err ? (
        <p className="text-sm text-center text-destructive">{err}</p>
      ) : null}
      <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
        <Button
          size="lg"
          disabled={busy || choiceLocked}
          className="w-full sm:w-auto"
          style={{ backgroundColor: ctx.acceptColor || undefined }}
          onClick={() => postAction("yes")}
        >
          {ctx.acceptText}
        </Button>
        <Button
          size="lg"
          variant="outline"
          disabled={busy || choiceLocked}
          className="w-full sm:w-auto"
          onClick={() => postAction("no")}
        >
          {ctx.declineText}
        </Button>
      </div>
    </div>
  );
}
